import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "../../lib/utils.ts";
import { ExternalLink } from "lucide-react";

interface SidebarLinkProps {
  title: string;
  icon: React.ReactNode;
  path?: string;
  href?: string;
  onClick?: () => void;
  external?: boolean;
  isCollapsed: boolean;
}

export const SidebarLink = ({
  title,
  icon,
  path,
  href,
  onClick,
  external,
  isCollapsed,
}: SidebarLinkProps) => {
  const { pathname: currentPath } = useLocation();

  const isActive = useMemo(() => {
    if (!currentPath || !path) {
      return false;
    }
    if (path.endsWith("*")) {
      return currentPath.includes(path.slice(0, -1));
    }
    return currentPath.includes(path);
  }, [currentPath, path]);

  const linkContent = (
    // `gap-0` when collapsed: the label collapses to `w-0` rather than
    // unmounting (so it can animate), and a gap still reserves its 8px beside
    // a zero-width element. Left in, every icon sits 4px right of where it
    // looks like it should, which reads as misalignment once the active row
    // draws a border around it.
    <div
      className={cn(
        "flex items-center min-w-0",
        isCollapsed ? "gap-0" : "gap-2",
      )}
    >
      <div className="flex-shrink-0">{icon}</div>
      <span
        className={cn(
          "truncate whitespace-nowrap transition-all duration-300",
          isCollapsed ? "w-0 opacity-0 overflow-hidden" : "w-auto opacity-100",
        )}
      >
        {title}
      </span>
      {external && !isCollapsed && (
        <ExternalLink className="h-3 w-3 flex-shrink-0 text-stone-400" />
      )}
    </div>
  );

  const linkClasses = cn(
    "flex items-center p-3 text-sm hover:bg-stone-50 transition-colors duration-200 border border-transparent",
    // Collapsed rows still stretch to the full 64px rail, so the icon has to be
    // centred within the row rather than left-aligned after the padding --
    // otherwise the active row's border is centred and its icon is not.
    isCollapsed ? "justify-center" : "",
    isActive ? "bg-stone-50 border-stone-200" : "",
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(linkClasses, "w-full text-left cursor-pointer")}
      >
        {linkContent}
      </button>
    );
  }

  if (href) {
    return (
      <a
        className={linkClasses}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {linkContent}
      </a>
    );
  }

  return (
    <Link to={path!} className={linkClasses}>
      {linkContent}
    </Link>
  );
};
